/**
 * Static fallback shapes for chat welcome suggestions.
 * Runtime UI loads from GET /mcp/v1/chat/suggestions (DB).
 * Keep seed data in mizfatools_mcp `faq.seed.ts` in sync when changing defaults.
 */

import type {
  McpChatSuggestionInput,
  McpChatSuggestionInteractionMode
} from '../types/mcpApiTypes';

export type McpChatSuggestionCategoryId =
  | 'domains'
  | 'keyword_tracker'
  | 'keyword_research'
  | 'indexer'
  | 'general';

export type McpChatSuggestionCategory = {
  id: McpChatSuggestionCategoryId;
  label: string;
};

export type McpChatSuggestion = {
  id: string;
  categoryId: McpChatSuggestionCategoryId;
  /** Short question shown on the chip (clear, not cryptic). */
  label: string;
  /** Full message sent to the chat when clicked (ignored if composeText is set). */
  prompt: string;
  /**
   * If set, the text is placed in the composer (not sent) so the user can
   * type their keyword(s) and send themselves.
   */
  composeText?: string;
  interactionMode?: McpChatSuggestionInteractionMode;
  inputs?: McpChatSuggestionInput[];
};

export const MCP_CHAT_SUGGESTION_CATEGORIES: McpChatSuggestionCategory[] = [
  { id: 'domains', label: 'دامنه‌ها' },
  { id: 'keyword_tracker', label: 'ردیاب کلمات' },
  { id: 'keyword_research', label: 'تحقیق کلمات' },
  { id: 'indexer', label: 'ایندکسر' },
  { id: 'general', label: 'عمومی' }
];

export const MCP_CHAT_SUGGESTIONS: McpChatSuggestion[] = [
  {
    id: 'domains-list',
    categoryId: 'domains',
    label: 'دامنه‌های ثبت‌شده در پنل من را لیست کن',
    prompt:
      'لطفاً لیست دامنه‌هایی که در حساب من ثبت شده را با نام نمایشی و آدرس نشان بده.',
    interactionMode: 'send'
  },
  {
    id: 'domains-keywords',
    categoryId: 'keyword_tracker',
    label: 'کلمات کلیدی ردیابی‌شده دامنه را با رتبه نشان بده',
    prompt:
      'کلمات کلیدی ردیابی‌شده روی دامنه {{domain_slug}} را همراه با رتبه فعلی در گوگل نشان بده. اگر رتبه‌ای نداشت، بنویس «بدون رتبه».',
    interactionMode: 'collect',
    inputs: [
      { key: 'domain_slug', label: 'دامنه', type: 'domain', required: true }
    ]
  },
  {
    id: 'domains-related',
    categoryId: 'keyword_research',
    label: 'کلمات مرتبط پیشنهادی برای دامنه‌ام را بیاور',
    prompt:
      'کلمات کلیدی مرتبط و پیشنهادی برای دامنه {{domain_slug}} را لیست کن و اگر میزان جستجو یا روند دارد، خلاصه بگو.',
    interactionMode: 'collect',
    inputs: [
      { key: 'domain_slug', label: 'دامنه', type: 'domain', required: true }
    ]
  },
  {
    id: 'keyword-planner-seed',
    categoryId: 'keyword_research',
    label: 'ایده کلمه کلیدی بگیر — کلمه‌ات را بنویس',
    prompt: '',
    composeText:
      'با کیورد پلنر برای این کلمه یا کلمات ایده بده (کلمه‌ات را این‌جا بنویس): ',
    interactionMode: 'compose'
  },
  {
    id: 'search-volume-list',
    categoryId: 'keyword_research',
    label: 'میزان جستجوی چند کلمه را یکجا بگو',
    prompt:
      'میزان جستجوی ماهانه این کلمات را بگیر و مقایسه کوتاهی داشته باش: {{keywords}}',
    interactionMode: 'collect',
    inputs: [
      {
        key: 'keywords',
        label: 'کلمات کلیدی',
        type: 'textarea',
        required: true,
        placeholder: 'هر سطر یک کلمه',
        minLines: 2,
        maxLines: 20
      }
    ]
  },
  {
    id: 'keyword-research-seeds',
    categoryId: 'keyword_research',
    label: 'برای این کلمات تحقیق کلمات کلیدی انجام بده',
    prompt: 'برای این کلمات تحقیق کلمات کلیدی انجام بده:\n{{keywords}}',
    interactionMode: 'collect',
    inputs: [
      {
        key: 'keywords',
        label: 'کلمات کلیدی',
        type: 'textarea',
        required: true,
        placeholder: 'هر سطر یک کلمه (حداکثر ۵)',
        minLines: 1,
        maxLines: 5
      }
    ]
  },
  {
    id: 'indexer-check-project',
    categoryId: 'indexer',
    label: 'آیا برای دامنه‌ام پروژه ایندکسر ساخته‌ام؟',
    prompt:
      'برای دامنه {{domain_slug}} چک کن آیا پروژه ایندکسر دارم. اگر ندارم بگو چطور بسازم.',
    interactionMode: 'collect',
    inputs: [
      { key: 'domain_slug', label: 'دامنه', type: 'domain', required: true }
    ]
  },
  {
    id: 'indexer-index-urls',
    categoryId: 'indexer',
    label: 'این URLها را برای ایندکس گوگل بفرست',
    prompt:
      'برای دامنه {{domain_slug}} این آدرس‌ها را برای ایندکس گوگل ارسال کن:\n{{urls}}',
    interactionMode: 'collect',
    inputs: [
      { key: 'domain_slug', label: 'دامنه', type: 'domain', required: true },
      {
        key: 'urls',
        label: 'آدرس صفحات',
        type: 'textarea',
        required: true,
        placeholder: 'هر سطر یک URL کامل',
        minLines: 1,
        maxLines: 50
      }
    ]
  },
  {
    id: 'indexer-urls-status',
    categoryId: 'indexer',
    label: 'وضعیت ایندکس صفحات دامنه‌ام را بگو',
    prompt:
      'برای دامنه {{domain_slug}} وضعیت ایندکس صفحات را خلاصه کن (چند تا ایندکس شده / نشده).',
    interactionMode: 'collect',
    inputs: [
      { key: 'domain_slug', label: 'دامنه', type: 'domain', required: true }
    ]
  },
  {
    id: 'general-capabilities',
    categoryId: 'general',
    label: 'چه ابزارهایی داری و برای چه کارهایی کمک می‌کنی؟',
    prompt:
      'چه ابزارهایی در اختیار داری (دامنه، ردیاب کلمات، تحقیق کلمات و غیره) و با یک مثال کوتاه بگو هر کدام چه کاری انجام می‌دهد؟',
    interactionMode: 'send'
  }
];

/** How many FAQ chips to show before the "more" control. */
export const MCP_CHAT_SUGGESTIONS_PREVIEW_COUNT = 4;

/** Flat shape for assistant-ui Suggestions config / follow-ups. */
export function toAuiSuggestions(
  items: McpChatSuggestion[] = MCP_CHAT_SUGGESTIONS
) {
  const categoryLabel = Object.fromEntries(
    MCP_CHAT_SUGGESTION_CATEGORIES.map(c => [c.id, c.label])
  ) as Record<McpChatSuggestionCategoryId, string>;

  return items
    .filter(item => {
      const mode = item.interactionMode ?? (item.composeText ? 'compose' : 'send');
      if (mode === 'collect') return Boolean(item.inputs?.length && item.prompt.trim());
      if (mode === 'compose') return Boolean(item.composeText?.trim());
      return Boolean(item.prompt.trim());
    })
    .map(item => ({
      title: categoryLabel[item.categoryId] ?? item.categoryId,
      label: item.label,
      prompt: item.composeText?.trim() || item.prompt
    }));
}
